import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../context/ToastContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Stack,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  List as ListIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  AccountBalance as FinanceIcon,
} from '@mui/icons-material';
import {
  fetchEvents,
  fetchCalendarEvents,
  fetchEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  publishEvent,
  cancelEvent,
  fetchEventCostsSummary,
  fetchEventRegistrations,
  clearCurrentEvent,
} from '../slices/eventsSlice';
import EventCalendarView from '../components/events/EventCalendarView';
import EventList from '../components/events/EventList';
import EventDialog from '../components/events/EventDialog';
import EventDetails from '../components/events/EventDetails';
import EventsFinanceOverview from '../components/events/EventsFinanceOverview';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);
const CAIRO = 'Cairo, Tajawal, sans-serif';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EventsPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { events, calendarEvents, currentEvent, loading, error } = useSelector((state) => state.events);
  
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [filters, setFilters] = useState({
    event_type: '',
    start_date: '',
    end_date: '',
    is_published: '',
    is_cancelled: '',
  });
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadEvents();
    loadCalendarEvents();
  }, [filters, currentMonth, currentYear]);

  const loadEvents = () => {
    dispatch(fetchEvents(filters));
  };

  const loadCalendarEvents = () => {
    dispatch(fetchCalendarEvents({ month: currentMonth, year: currentYear }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCreateEvent = () => {
    setSelectedEventId(null);
    setEventDialogOpen(true);
  };

  const handleEditEvent = (eventId) => {
    setSelectedEventId(eventId);
    dispatch(fetchEvent(eventId)).then(() => {
      setEventDialogOpen(true);
    });
  };

  const handleViewEvent = (eventId) => {
    setSelectedEventId(eventId);
    dispatch(fetchEvent(eventId)).then(() => {
      dispatch(fetchEventCostsSummary(eventId));
      dispatch(fetchEventRegistrations(eventId));
      setEventDetailsOpen(true);
    });
  };

  const handleSaveEvent = async (eventData) => {
    try {
      if (selectedEventId) {
        await dispatch(updateEvent({ id: selectedEventId, data: eventData })).unwrap();
        showToast('تم تحديث الفعالية بنجاح', 'success');
      } else {
        await dispatch(createEvent(eventData)).unwrap();
        showToast('تم إنشاء الفعالية بنجاح', 'success');
        // Clear filters that might exclude the new event to ensure it's visible
        setFilters({
          event_type: '',
          start_date: '',
          end_date: '',
          is_published: '',
          is_cancelled: '',
        });
      }
      setEventDialogOpen(false);
      setSelectedEventId(null);
      // Refresh events list - use a small delay to ensure server has committed
      await new Promise(resolve => setTimeout(resolve, 300));
      loadEvents();
      loadCalendarEvents();
    } catch (error) {
      showToast(error?.message || 'حدث خطأ أثناء حفظ الفعالية', 'error');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الفعالية؟')) {
      try {
        await dispatch(deleteEvent(eventId)).unwrap();
        showToast('تم حذف الفعالية بنجاح', 'success');
        loadEvents();
        loadCalendarEvents();
      } catch (error) {
        showToast(error?.message || 'حدث خطأ أثناء حذف الفعالية', 'error');
      }
    }
  };

  const handlePublishEvent = async (eventId, isPublished) => {
    try {
      await dispatch(publishEvent({ id: eventId, is_published: isPublished })).unwrap();
      showToast(`تم ${isPublished ? 'نشر' : 'إلغاء نشر'} الفعالية بنجاح`, 'success');
      loadEvents();
      loadCalendarEvents();
    } catch (error) {
      showToast(error?.message || 'حدث خطأ', 'error');
    }
  };

  const handleCancelEvent = async (eventId) => {
    if (window.confirm('هل أنت متأكد من إلغاء هذه الفعالية؟')) {
      try {
        await dispatch(cancelEvent(eventId)).unwrap();
        showToast('تم إلغاء الفعالية بنجاح', 'success');
        loadEvents();
        loadCalendarEvents();
      } catch (error) {
        showToast(error?.message || 'حدث خطأ', 'error');
      }
    }
  };

  // Calculate stats
  const stats = {
    total: events.length,
    upcoming: events.filter(e => new Date(e.start_date) >= new Date() && !e.is_cancelled).length,
    published: events.filter(e => e.is_published && !e.is_cancelled).length,
    cancelled: events.filter(e => e.is_cancelled).length,
  };

  const statCards = [
    { label: 'إجمالي الفعاليات', value: stats.total, icon: <EventIcon />, color: '#4338CA', light: '#EEF2FF' },
    { label: 'فعاليات قادمة', value: stats.upcoming, icon: <CalendarIcon />, color: '#16A34A', light: '#DCFCE7' },
    { label: 'منشورة', value: stats.published, icon: <EventIcon />, color: '#0284C7', light: '#E0F2FE' },
    { label: 'ملغاة', value: stats.cancelled, icon: <EventIcon />, color: '#DC2626', light: '#FEE2E2' },
  ];

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: CAIRO,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EventIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: CAIRO }}>
                إدارة الفعاليات
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: CAIRO }}
              >
                إنشاء وتنظيم ومتابعة الفعاليات المدرسية
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateEvent}
            data-guide-id="guide-add-event"
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: CAIRO,
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة فعالية
          </Button>
        </MotionPaper>

        {/* Stats Cards */}
        <Grid
          container
          spacing={2}
          sx={{ mb: 3 }}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        >
          {statCards.map((s) => (
            <Grid item xs={12} sm={6} md={3} key={s.label}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  bgcolor: '#fff',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                  border: '1px solid #E5E7EB',
                  transition: 'transform .2s ease, box-shadow .2s ease',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 20px rgba(67,56,202,0.12)' },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack useFlexGap direction="row" alignItems="center" spacing={1.75}>
                    <Box
                      sx={{
                        bgcolor: s.light,
                        color: s.color,
                        borderRadius: 2.5,
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#1E1B4B', fontFamily: CAIRO, lineHeight: 1.1 }}>
                        {s.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500, fontFamily: CAIRO }}>
                        {s.label}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', mb: 2, fontFamily: CAIRO }}>
            تصفية الفعاليات
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={{ fontFamily: CAIRO }}>
                <InputLabel>نوع الفعالية</InputLabel>
                <Select
                  value={filters.event_type}
                  label="نوع الفعالية"
                  onChange={(e) => setFilters({ ...filters, event_type: e.target.value })}
                  sx={{ borderRadius: 2, fontFamily: CAIRO }}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="assembly">اجتماع</MenuItem>
                  <MenuItem value="holiday">عطلة</MenuItem>
                  <MenuItem value="exam_period">فترة امتحانات</MenuItem>
                  <MenuItem value="trip">رحلة</MenuItem>
                  <MenuItem value="celebration">احتفال</MenuItem>
                  <MenuItem value="meeting">اجتماع</MenuItem>
                  <MenuItem value="workshop">ورشة عمل</MenuItem>
                  <MenuItem value="sports_day">يوم رياضي</MenuItem>
                  <MenuItem value="graduation">تخرج</MenuItem>
                  <MenuItem value="other">أخرى</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="من تاريخ"
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, fontFamily: CAIRO }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="إلى تاريخ"
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, fontFamily: CAIRO }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={filters.is_published}
                  label="الحالة"
                  onChange={(e) => setFilters({ ...filters, is_published: e.target.value })}
                  sx={{ borderRadius: 2, fontFamily: CAIRO }}
                >
                  <MenuItem value="">الكل</MenuItem>
                  <MenuItem value="true">منشور</MenuItem>
                  <MenuItem value="false">غير منشور</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: '1px solid #E5E7EB',
              px: 1,
              '& .MuiTab-root': {
                fontFamily: CAIRO,
                fontWeight: 700,
                textTransform: 'none',
                color: '#6B7280',
                minHeight: 56,
              },
              '& .Mui-selected': { color: '#4338CA !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#4338CA', height: 3, borderRadius: 3 },
            }}
          >
            <Tab icon={<CalendarIcon />} iconPosition="start" label="عرض التقويم" />
            <Tab icon={<ListIcon />} iconPosition="start" label="عرض القائمة" />
            <Tab icon={<FinanceIcon />} iconPosition="start" label="المالية" />
          </Tabs>
          <TabPanel value={activeTab} index={0}>
          <EventCalendarView
            events={calendarEvents}
            loading={loading}
            currentMonth={currentMonth}
            currentYear={currentYear}
            onMonthChange={(month, year) => {
              setCurrentMonth(month);
              setCurrentYear(year);
            }}
            onEventClick={handleViewEvent}
            onEventCreate={(date) => {
              setSelectedEventId(null);
              setEventDialogOpen(true);
            }}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <EventList
            events={events}
            loading={loading}
            onView={handleViewEvent}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
            onPublish={handlePublishEvent}
            onCancel={handleCancelEvent}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <EventsFinanceOverview events={events} />
        </TabPanel>
      </Paper>

      {/* Event Dialog */}
      <EventDialog
        open={eventDialogOpen}
        onClose={() => {
          setEventDialogOpen(false);
          setSelectedEventId(null);
          dispatch(clearCurrentEvent());
        }}
        onSave={handleSaveEvent}
        event={currentEvent}
      />

      {/* Event Details Dialog */}
      <EventDetails
        open={eventDetailsOpen}
        onClose={() => {
          setEventDetailsOpen(false);
          setSelectedEventId(null);
          dispatch(clearCurrentEvent());
        }}
        eventId={selectedEventId}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onPublish={handlePublishEvent}
        onCancel={handleCancelEvent}
      />
      </Box>
    </Box>
  );
};

export default EventsPage;
